/* eslint-disable @next/next/no-img-element */
import { ThemeProvider, Typography } from "@sunday/component";
import { useTranslation } from "next-i18next";
import styled from "styled-components";
import { ICTH_THEME } from "src/components/Common/ICTHLayout/constants";

const MAP_EMBED_URL =
  "https://www.google.com/maps?q=13.7191001%2C100.5214328&z=17&output=embed";

const Page = styled.main`
  --contact-heading-font-family: var(--typescale-font-en);
  --contact-banner-font-weight: 700;
  --contact-banner-tracking: var(--typescale-tracking-en-d-3);
  --contact-banner-mobile-tracking: var(--typescale-tracking-en-h-2);
  --contact-link-color: var(--color-text-information, #1561e8);
  --contact-supporting-text-color: var(--color-text-secondary, #5f6468);
  --typescale-weight-heading-bold: 700;

  width: 100%;
  background: var(--color-surface-default, #ffffff);

  &:lang(th) {
    --contact-heading-font-family: var(--typescale-font-th);
    --contact-banner-font-weight: 600;
    --contact-banner-tracking: var(--typescale-tracking-th);
    --contact-banner-mobile-tracking: var(--typescale-tracking-th);
  }
`;

const Banner = styled.section`
  position: relative;
  display: flex;
  width: 100%;
  height: 134px;
  overflow: hidden;
  background: linear-gradient(
    90deg,
    #d8ddff 0%,
    var(--color-surface-ci-pri-bright, #feece8) 100%
  );

  &::after {
    position: absolute;
    inset: 0;
    background-image: url("/static/pages/contact-us/banner.png");
    background-position: center;
    background-size: cover;
    content: "";
    mix-blend-mode: multiply;
    opacity: 0.1;
    pointer-events: none;
  }

  @media (max-width: 767px) {
    height: 80px;

    &::after {
      background-position: 40% center;
    }
  }
`;

const BannerInner = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  width: min(100% - 80px, 1388px);
  margin: 0 auto;

  @media (max-width: 767px) {
    width: calc(100% - 32px);
  }
`;

const BannerTitle = styled(Typography.Display).attrs({
  designLevel: 3,
  weight: "bold",
})`
  && {
    --typography-heading-font-family: var(--contact-heading-font-family);
    --typography-heading-font-weight-bold: var(--contact-banner-font-weight);
    --typography-d3-font-size: var(--typescale-size-d-3);
    --typography-d3-line-height: var(--typescale-line-height-d-3);

    margin: 0;
    color: var(--color-text-primary, #202123);
    font-style: normal;
    letter-spacing: var(--contact-banner-tracking);
    vertical-align: middle;
  }

  @media (max-width: 767px) {
    && {
      --typography-d3-font-size: var(--typeface-size-sd-h-2);
      --typography-d3-line-height: var(--typescale-line-height-h-2);

      letter-spacing: var(--contact-banner-mobile-tracking);
    }
  }
`;

const Content = styled.section`
  display: grid;
  grid-template-areas:
    "heading map"
    "company map"
    "service map"
    "methods map";
  grid-template-columns: minmax(0, 569fr) minmax(0, 779fr);
  /* Let the map extend the last row without spreading out the text rows. */
  grid-template-rows: repeat(3, min-content) 1fr;
  align-items: start;
  column-gap: 40px;
  row-gap: 24px;
  width: min(100% - 80px, 1388px);
  min-height: 639px;
  margin: 0 auto;
  padding: 80px 0 120px;

  @media (max-width: 767px) {
    grid-template-areas:
      "heading"
      "map"
      "company"
      "service"
      "methods";
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: repeat(5, min-content);
    width: calc(100% - 32px);
    min-height: 0;
    padding: 40px 0 80px;
  }
`;

const ContactHeading = styled(Typography.Headline).attrs({
  designLevel: 1,
  level: 2,
  weight: "bold",
})`
  && {
    --typography-heading-font-family: var(--contact-heading-font-family);
    --typography-heading-font-weight-bold: 700;
    --typography-h1-font-size: var(--typeface-size-sd-h-1);
    --typography-h1-line-height: var(--typeface-line-height-sd-h-1);

    grid-area: heading;
    margin: 0;
    color: var(--color-text-primary, #202123);
    font-style: normal;
    letter-spacing: var(--typescale-tracking-en-h-1);
    vertical-align: middle;
  }
`;

const Company = styled.div`
  display: flex;
  grid-area: company;
  flex-direction: column;
  gap: 8px;
`;

const SectionTitle = styled(Typography.Headline).attrs({
  designLevel: 3,
  level: 3,
  weight: "bold",
})`
  && {
    --typography-heading-font-family: var(--typescale-font-head);

    margin: 0;
  }
`;

const ServiceTitle = styled(SectionTitle)`
  grid-area: service;
`;

const BodyText = styled(Typography.Body).attrs({
  designLevel: 1,
  weight: "regular",
})`
  && {
    --typography-base-font-family: var(--typescale-font-base);
    --typography-base-font-weight-regular: 400;
    --typography-b1-font-size: var(--typescale-size-b-1);
    --typography-b1-line-height: var(--typescale-line-height-b-1);
    --typography-color: var(--contact-supporting-text-color);

    margin: 0;
    color: var(--contact-supporting-text-color);
    font-style: normal;
    font-weight: 400;
    letter-spacing: var(--typescale-tracking-base);
    vertical-align: middle;
  }
`;

const MapFrame = styled.div`
  position: relative;
  grid-area: map;
  width: 100%;
  aspect-ratio: 580 / 322;
  overflow: hidden;
  border: 1px solid var(--color-stroke-default, #d9dbde);
  border-radius: 16px;

  iframe {
    display: block;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const ContactMethods = styled.div`
  display: flex;
  grid-area: methods;
  flex-direction: column;
  gap: 8px;
`;

const ContactMethod = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
`;

const ContactIcon = styled.img`
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
`;

const ContactCopy = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
`;

const LinkText = styled(Typography.Hyperlink).attrs({
  designLevel: 1,
  weight: "regular",
})`
  && {
    --typography-base-font-family: var(--typescale-font-base);
    --typography-base-font-weight-regular: 400;
    --typography-b1-font-size: var(--typescale-size-b-1);
    --typography-b1-line-height: var(--typescale-line-height-b-1);
    --typography-color: var(--contact-link-color);
    --typography-hyperlink-color-hover: var(--contact-link-color);

    display: block;
    width: fit-content;
    margin: 0;
    color: var(--contact-link-color);
    font-style: normal;
    font-weight: 400;
    letter-spacing: var(--typescale-tracking-base);
    text-decoration: underline;
    text-decoration-style: solid;
    vertical-align: middle;
  }

  &&:hover,
  &&:focus,
  &&:active {
    color: var(--contact-link-color);
  }
`;

type ContactMethodRowProps = {
  iconSrc: string;
  linkHref: string;
  linkLabel: string;
  hours: string;
  external: boolean;
};

const ContactMethodRow = ({
  iconSrc,
  linkHref,
  linkLabel,
  hours,
  external,
}: ContactMethodRowProps) => (
  <ContactMethod>
    <ContactIcon src={iconSrc} alt="" aria-hidden="true" />
    <ContactCopy>
      <LinkText
        href={linkHref}
        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      >
        {linkLabel}
      </LinkText>
      <BodyText>{hours}</BodyText>
    </ContactCopy>
  </ContactMethod>
);

const ContactUsPage = (): JSX.Element => {
  const { t, i18n } = useTranslation("contact-us");
  const language = i18n.resolvedLanguage || i18n.language;

  return (
    <ThemeProvider theme={ICTH_THEME}>
      <Page lang={language}>
        <Banner aria-labelledby="contact-us-banner-title">
          <BannerInner>
            <BannerTitle id="contact-us-banner-title">
              {t("bannerTitle")}
            </BannerTitle>
          </BannerInner>
        </Banner>

        <Content>
          <ContactHeading>{t("heading")}</ContactHeading>

          <MapFrame>
            <iframe
              src={MAP_EMBED_URL}
              title={t("mapTitle")}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </MapFrame>

          <Company>
            <SectionTitle>{t("company.name")}</SectionTitle>
            <BodyText>{t("company.address")}</BodyText>
          </Company>

          <ServiceTitle>{t("customerService.title")}</ServiceTitle>

          <ContactMethods>
            <ContactMethodRow
              iconSrc="/static/pages/contact-us/phone-filled.svg"
              linkHref="tel:+6620263355"
              linkLabel={t("customerService.phone.value")}
              hours={t("customerService.phone.hours")}
              external={false}
            />
            <ContactMethodRow
              iconSrc="/static/pages/contact-us/mail-filled.svg"
              linkHref="mailto:hello@icecream.co.th"
              linkLabel={t("customerService.email.value")}
              hours={t("customerService.email.hours")}
              external={false}
            />
            <ContactMethodRow
              iconSrc="/static/pages/contact-us/line-messenger.svg"
              linkHref="https://line.me/R/ti/p/@icecreambrokerth"
              linkLabel={t("customerService.line.value")}
              hours={t("customerService.line.hours")}
              external
            />
          </ContactMethods>
        </Content>
      </Page>
    </ThemeProvider>
  );
};

export default ContactUsPage;
